import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  TextField,
  Typography,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button as MuiButton,
  Drawer,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import {
  Error as ErrorIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon,
  FileCopyOutlined as CopyIcon,
  CheckOutlined as CheckIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Stars as StarsIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  Menu as MenuIcon,
  ExitToApp as ExitToAppIcon,
  ThumbUpOutlined as ThumbUpIcon,
  ThumbDownOutlined as ThumbDownIcon
} from '@material-ui/icons';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardFooter } from './Card';
import { processQuestion, getQueryHistory, getLocalQueryHistory, getQueryHistoryByThread, deleteQueryHistory, deleteLocalQueryHistoryItem, QueryHistory, ProcessedResult, ConversationMessage } from '../../services';
import speechService from '../../services/SpeechService';
import { LAYOUT } from '../../constants';

const SIDEBAR_WIDTH = 287;

const useStyles = makeStyles((theme) => ({
  // Layout and History sidebar styles
  drawerContainer: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
  },
  drawerContainerPWA: {
    position: 'relative',
    display: 'flex',
    flex: 0,
    overflow: 'visible',
    minHeight: 0,
    height: 0,
  },
  toggleButton: {
    position: 'fixed',
    left: `${SIDEBAR_WIDTH - 17}px`,
    top: '75px',
    zIndex: 1200,
    color: '#fff',
    background: 'var(--color-primary)',
    '&:hover': {
      color: '#fff',
      background: 'var(--color-primary)',
    },
  },
  toggleButtonCollapsed: {
    position: 'fixed',
    left: '1px',
    top: '75px',
    zIndex: 1200,
    color: '#fff',
    background: 'var(--color-primary)',
    '&:hover': {
      color: '#fff',
      background: 'var(--color-primary)',
    },
  },
  drawerPaper: {
    overflowY: 'auto',
    overflowX: 'hidden',
    background: 'var(--color-sidebar-bg)',
    top: LAYOUT.HeaderHeight,
    bottom: 0,
    width: `${SIDEBAR_WIDTH}px`,
    maxWidth: `${SIDEBAR_WIDTH}px`,
    height: 'unset',
    '&::-webkit-scrollbar': {
      width: 5,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(255,255,255,0.2)',
      borderRadius: 100,
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    position: 'relative',
    flex: 1,
    overflow: 'hidden',
    alignItems: 'stretch',
    flexWrap: 'nowrap',
    minHeight: 0,
  },
  sidebarHeader: {
    padding: '20px 20px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    backgroundColor: 'var(--color-sidebar-bg)',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },

  newChatLink: {
    height: 32,
    padding: '0 14px',
    border: 'none',
    borderRadius: 8,
    backgroundColor: 'var(--color-primary)',
    color: '#fff',
    fontSize: '0.8rem',
    fontWeight: 400,
    textTransform: 'none',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: '#ff8657',
    },
    '& .MuiButton-startIcon': {
      marginRight: 6,
    },
  },
  startNewChatButton: {
    margin: '12px 24px',
    backgroundColor: 'var(--color-primary)',
    color: '#ffffff',
    borderRadius: '8px',
    padding: theme.spacing(1.5),
    textTransform: 'none',
    fontWeight: 600,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: '#c45a1f',
    },
  },
  searchInputContainer: {
    padding: '8px 24px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    position: 'sticky',
    top: '56px',
    backgroundColor: 'var(--color-sidebar-bg)',
    zIndex: 9,
  },
  historySearchInput: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255,255,255,0.08)',
      borderRadius: '6px',
      fontSize: '0.875rem',
      color: '#fff',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
      '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 12px',
      '&::placeholder': { color: 'rgba(255,255,255,0.4)' },
    },
    '& .MuiInputAdornment-root': {
      marginRight: '4px',
      color: 'rgba(255,255,255,0.4)',
    },
  },
  sidebarTitle: {
    fontSize: '1.05rem',
    color: '#fff',
    letterSpacing: '0.02em',
  },
  sidebarContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: 0,
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255,255,255,0.2)',
      borderRadius: '3px',
    },
  },
  historyItem: {
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    padding: '14px 20px',
    margin: 0,
    borderRadius: 0,
    transition: 'background-color 0.15s ease',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.12)',
      borderColor: 'rgba(255,255,255,0.15)',
      '& $historyItemDelete': {
        opacity: 1,
      },
    },
  },
  historyItemActive: {
    backgroundColor: 'rgba(223, 109, 46, 0.15) !important',
    borderLeft: '3px solid var(--color-primary) !important',
    '&:hover': {
      backgroundColor: 'rgba(223, 109, 46, 0.2) !important',
    },
  },
  historyItemDelete: {
    opacity: 0,
    transition: 'opacity 0.15s ease',
    '&:hover': {
      color: 'rgba(255,255,255,0.8) !important',
    },
  },
  historyChevron: {
    transition: 'opacity 0.15s ease',
  },
  historyItemContent: {
    flex: 1,
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateX(4px)',
    },
  },
  deleteButton: {
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    color: 'rgba(255,255,255,0.4)',
    '&:hover': {
      color: '#ef5350',
      backgroundColor: 'rgba(244, 67, 54, 0.15)',
    },
  },
  historyQuery: {
    fontWeight: 400,
    fontSize: '0.875rem',
    color: '#fff',
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    paddingRight: 8,
  },
  historyPreview: {
    fontSize: '0.75rem',
    color: 'rgba(255,255,255,0.45)',
    marginTop: 4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  historyDate: {
    fontSize: '0.7rem',
    color: 'rgba(255,255,255,0.35)',
    marginTop: 4,
  },
  historyFilters: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100vh - ${LAYOUT.HeaderHeight}px)`,
    overflow: 'hidden',
    position: 'relative',
    minWidth: 0,
    transition: 'margin-left 0.3s ease',
  },
  thinkingIcon: {
    animation: '$pulse 1.5s infinite ease-in-out',
    color: theme.palette.primary.main,
  },
  thinkingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-primary)',
    display: 'inline-block',
    marginRight: '4px',
    animation: '$pulse 1.5s infinite ease-in-out',
  },
  thinkingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    minWidth: '100px',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 0.6,
      transform: 'scale(0.9)',
    },
    '50%': {
      opacity: 1,
      transform: 'scale(1.1)',
    },
    '100%': {
      opacity: 0.6,
      transform: 'scale(0.9)',
    },
  },
  sendButton: {
    borderRadius: 12,
    padding: theme.spacing(0, 3),
    height: 44,
    backgroundColor: 'var(--color-primary)',
    color: '#fff',
    fontWeight: 400,
    textTransform: 'none',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: '#ff8657',
      boxShadow: '0 4px 12px rgba(223, 109, 46, 0.3)',
    },
    '&:disabled': {
      opacity: 0.4,
      backgroundColor: 'var(--color-primary)',
      color: '#fff',
      boxShadow: 'none',
      cursor: 'not-allowed',
    },
    minWidth: '90px',
    fontSize: '0.85rem',
    flexShrink: 0,
  },
  inputHelperText: {
    fontSize: '0.75rem',
    color: 'var(--color-text-secondary)',
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  noHistory: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: 'rgba(255,255,255,0.5)',
  },
  refreshButton: {
    marginLeft: 'auto',
  },
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--color-page-bg)',
  },
  card: {
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    border: 'none',
  },
  cardPWA: {
    boxShadow: 'none !important' as any,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '0 !important' as any,
    margin: 0,
    padding: 0,
  },
  title: {
    fontWeight: 600,
  },
  suggestedSection: {
    padding: theme.spacing(1, 0, 0.5),
    maxWidth: 900,
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  suggestedLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'rgba(0,0,0,0.4)',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  suggestedGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1),
  },
  suggestedChip: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '0.8rem',
    color: 'rgba(0,0,0,0.7)',
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: 'var(--color-primary)',
      backgroundColor: 'rgba(223, 109, 46, 0.04)',
    },
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3),
    backgroundColor: '#f8f9f9',
    borderTop: 'none',
    boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
    flexShrink: 0,
    zIndex: 10,
    width: '100%',
    boxSizing: 'border-box',
  },
  textFieldContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    border: '1px solid #d1d5db',
    minHeight: 44,
    boxSizing: 'border-box',
    transition: 'all 0.2s',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    '&:focus-within': {
      borderColor: 'rgba(223, 109, 46, 0.5)',
      boxShadow: '0 0 0 2px rgba(223, 109, 46, 0.12)',
    },
  },
  inputIconButton: {
    width: 44,
    height: 44,
    border: '1px solid #d1d5db',
    borderRadius: 12,
    backgroundColor: '#ffffff',
    color: '#6b7280',
    flexShrink: 0,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: '#f9fafb',
      borderColor: 'rgba(223, 109, 46, 0.5)',
      color: 'var(--color-primary)',
    },
  },
  micRecording: {
    backgroundColor: '#fef2f2 !important',
    border: '2px solid #ef4444 !important',
    color: '#ef4444 !important',
    animation: '$micPulse 1.5s ease-in-out infinite',
    boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.4)',
  },
  '@keyframes micPulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.4)' },
    '70%': { boxShadow: '0 0 0 8px rgba(239, 68, 68, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(239, 68, 68, 0)' },
  },

  textField: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: 'transparent',
      border: 'none',
      padding: 0,
      '& fieldset': { border: 'none' },
      '&:hover fieldset': { border: 'none' },
      '&.Mui-focused fieldset': { border: 'none' },
    },
    '& .MuiOutlinedInput-inputMultiline': {
      padding: '11px 16px',
      lineHeight: '20px',
    },
  },
  hint: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  errorContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: 'rgba(211, 47, 47, 0.08)',
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
  },
  errorContent: {
    marginLeft: theme.spacing(1),
  },
  errorIcon: {
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  resultsContainer: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: '#f8f9f9',
    minHeight: 0,
    minWidth: 0,
  },
  analysisTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  filterChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
    width: '100%',
  },
  filterChip: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.75, 1.5),
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.primary,
    border: '1px solid rgba(0, 0, 0, 0.1)',
    fontWeight: 500,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  responseContainer: {
    padding: 0,
    backgroundColor: 'transparent',
    borderRadius: 0,
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'none',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '0px',
      background: 'linear-gradient(rgba(0, 0, 0, 0.05), transparent)',
      zIndex: 1,
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius,
    },
  },
  markdownContainer: {
    padding: theme.spacing(1, 0),
  },
  markdownBody: {
    fontSize: '0.9375rem',
    lineHeight: 1.7,
    color: '#1a1a1a',
    wordSpacing: '0.02em',
    '& h1, & h2, & h3': {
      marginTop: '1.2em',
      marginBottom: '0.5em',
      fontWeight: 600,
      lineHeight: 1.3,
      color: '#111',
    },
    '& h1': { fontSize: '1.25rem' },
    '& h2': { fontSize: '1.1rem' },
    '& h3': { fontSize: '1rem' },
    '& p': {
      marginTop: 0,
      marginBottom: '0.75em',
      lineHeight: 1.7,
    },
    '& ul, & ol': {
      paddingLeft: '1.5em',
      marginTop: '0.25em',
      marginBottom: '0.75em',
    },
    '& ul': { listStyleType: 'disc' },
    '& ol': { listStyleType: 'decimal' },
    '& li': {
      marginBottom: '0.4em',
      lineHeight: 1.6,
      paddingLeft: '0.25em',
    },
    '& li > p': { marginBottom: '0.25em' },
    '& strong': { fontWeight: 600 },
    '& em': { fontStyle: 'italic' },
    '& a': { color: 'var(--color-primary)', textDecoration: 'none' },
    '& code': {
      backgroundColor: '#f5f5f5',
      padding: '0.15em 0.4em',
      borderRadius: 4,
      fontSize: '0.85em',
      fontFamily: '"Fira Code", "Consolas", monospace',
    },
    '& pre': {
      backgroundColor: '#f5f5f5',
      padding: '12px 16px',
      borderRadius: 6,
      overflowX: 'auto',
      marginBottom: '0.75em',
      '& code': { backgroundColor: 'transparent', padding: 0 },
    },
    '& blockquote': {
      borderLeft: '3px solid var(--color-primary)',
      margin: '0.75em 0',
      padding: '0.5em 1em',
      color: '#555',
      backgroundColor: 'rgba(223, 109, 46, 0.04)',
      borderRadius: '0 4px 4px 0',
    },
    '& hr': {
      border: 'none',
      borderTop: '1px solid #e5e7eb',
      margin: '1em 0',
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginBottom: '0.75em',
      fontSize: '0.875rem',
    },
    '& th, & td': {
      border: '1px solid #e5e7eb',
      padding: '8px 12px',
      textAlign: 'left',
    },
    '& th': { backgroundColor: '#f9fafb', fontWeight: 600 },
  },
  // ChatGPT-style conversation message styles
  conversationContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(3, 4),
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  messageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0,
  },
  messageRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
  },
  messageRowUser: {
    justifyContent: 'flex-end',
  },
  messageRowAssistant: {
    justifyContent: 'flex-start',
  },
  messageAvatar: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'rgba(223, 109, 46, 0.1)',
    border: '1px solid rgba(223, 109, 46, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 2,
    color: '#df6d2e',
  },
  messageAvatarIcon: {
    color: '#df6d2e',
    fontSize: '1.25rem',
  },
  userAvatarIcon: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: 'transparent',
    border: '1.5px solid rgba(0,0,0,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 4,
    color: 'rgba(0,0,0,0.4)',
    fontSize: '0.85rem',
  },
  messageLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'rgba(0,0,0,0.5)',
    marginBottom: theme.spacing(0.5),
  },
  messageLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  messageTimestamp: {
    fontSize: '0.7rem',
    color: 'rgba(0,0,0,0.35)',
    fontWeight: 400,
  },
  userMessage: {
    maxWidth: '65%',
    backgroundColor: 'var(--color-primary)',
    color: '#fff',
    padding: theme.spacing(1.5, 2.5),
    borderRadius: '18px 18px 4px 18px',
    wordWrap: 'break-word',
    border: 'none',
    boxShadow: '0 2px 8px rgba(223, 109, 46, 0.2)',
  },
  assistantMessage: {
    maxWidth: '65%',
    backgroundColor: '#ffffff',
    padding: theme.spacing(2, 2.5),
    borderRadius: '18px 18px 18px 4px',
    border: '1px solid #e5e7eb',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    overflow: 'hidden',
    minWidth: 0,
    position: 'relative',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  messageActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
  },
  copyButton: {
    padding: 4,
    borderRadius: 6,
    color: 'var(--color-text-secondary)',
    '&:hover': {
      backgroundColor: 'var(--color-muted-bg)',
      color: 'var(--color-text-primary)',
    },
  },
  copyButtonIcon: {
    fontSize: 14,
  },
  messageRole: {
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    alignItems: 'flex-start',
    marginTop: '4px',
  },
  responseWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  // Original bullet point styles - kept for backward compatibility
  bulletPoint: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: 'flex-start',
  },
  bullet: {
    color: theme.palette.primary.main,
    flexShrink: 0,
    marginTop: '4px',
  },
  recommendationSection: {
    backgroundColor: 'rgba(223, 109, 46, 0.05)',
    padding: theme.spacing(2, 2.5),
    borderRadius: '10px',
    marginTop: theme.spacing(2),
  },
  recommendationTitle: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#df6d2e',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  recommendationContent: {
    marginTop: theme.spacing(1),
  },
  expandButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    marginTop: theme.spacing(1),
    cursor: 'pointer',
    color: 'var(--color-primary)',
    fontSize: '0.875rem',
    fontWeight: 500,
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: 'var(--color-primary-tint)',
      textDecoration: 'underline',
    },
  },
  welcomeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: theme.spacing(4),
    textAlign: 'center',
    overflow: 'auto',
    minHeight: 0,
  },
  welcomeIcon: {
    width: 72,
    height: 72,
    marginBottom: theme.spacing(3),
    borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(223, 109, 46, 0.25)',
  },
  welcomeTitle: {
    fontWeight: 700,
    fontSize: '1.75rem',
    color: 'var(--color-text-primary)',
    marginBottom: theme.spacing(1),
  },
  welcomeSubtitle: {
    fontSize: '1rem',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6,
    marginBottom: theme.spacing(4),
  },
  suggestionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(2),
    maxWidth: 700,
    width: '100%',
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  suggestionCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: 12,
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-panel-bg)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left',
    '&:hover': {
      borderColor: 'var(--color-primary)',
      backgroundColor: 'var(--color-primary-tint)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
    },
  },
  suggestionIcon: {
    color: 'var(--color-primary)',
    fontSize: 20,
    marginTop: 2,
    flexShrink: 0,
  },
  suggestionText: {
    fontSize: '0.875rem',
    color: 'var(--color-text-primary)',
    lineHeight: 1.5,
  },
}));

// Using ProcessedResult from services

export interface VectorSearchProps {
  selectedQuestion?: string;
  facilityNames?: string[];
  classificationNames?: string[];
  classificationTypes?: string[];
  startDate?: string;
  endDate?: string;
  isPWA?: boolean;
}



export const VectorSearch = forwardRef<unknown, VectorSearchProps>(function VectorSearch(
  {
    selectedQuestion: _selectedQuestion = '',
    facilityNames: propFacilityNames = [],
    classificationNames: propClassificationNames = [],
    classificationTypes: propClassificationTypes = [],
    startDate: propStartDate = '',
    endDate: propEndDate = '',
    isPWA = false,
  },
  ref
) {
  const classes = useStyles();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProcessedResult | null>(null);
  const [conversationMessages, setConversationMessages] = useState<ConversationMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPhrase, setLoadingPhrase] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(new Set());
  const [isRecording, setIsRecording] = useState(false);


  // Thread management - backend controls thread lifecycle
  const [threadId, setThreadId] = useState<string>('');

  // Local state for filters that can be updated from history
  const [localFacilityNames, setLocalFacilityNames] = useState<string[]>(propFacilityNames);
  const [localClassificationNames, setLocalClassificationNames] = useState<string[]>(propClassificationNames);
  const [localClassificationTypes, setLocalClassificationTypes] = useState<string[]>(propClassificationTypes);
  const [localStartDate, setLocalStartDate] = useState<string>(propStartDate);
  const [localEndDate, setLocalEndDate] = useState<string>(propEndDate);

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<QueryHistory | null>(null);

  // History sidebar state - PWA starts closed
  const [sidebarOpen, setSidebarOpen] = useState(!isPWA);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [queryHistory, setQueryHistory] = useState<QueryHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);

  useEffect(() => {
    setMounted(true);
    loadQueryHistory();
  }, []);

  // Auto-scroll chat to bottom when messages change or loading state changes
  useEffect(() => {
    const scrollToBottom = () => {
      if (chatScrollRef.current) {
        chatScrollRef.current.scrollTo({
          top: chatScrollRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    };
    // Immediate attempt + deferred attempt for cases where the container
    // is conditionally rendered and the ref isn't attached yet
    scrollToBottom();
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [conversationMessages, isLoading]);

  const toggleVoiceRecording = () => {
    if (isRecording) {
      speechService.stop();
      setIsRecording(false);
      return;
    }

    speechService.start({
      onRecognizing: (text) => setQuery(text),
      onRecognized: (text) => setQuery(text),
      onError: (err) => {
        console.error('Speech recognition error:', err);
        setError(err);
      },
      onStopped: () => setIsRecording(false),
    });
    setIsRecording(true);
  };

  // Load query history from server and localStorage
  const loadQueryHistory = async () => {
    setLoadingHistory(true);
    try {
      // Try to get history from server first
      const serverHistory = await getQueryHistory();
      setQueryHistory(serverHistory);
    } catch (error) {
      console.error('Failed to load history from server, falling back to localStorage:', error);
      // Fall back to localStorage
      const localHistory = getLocalQueryHistory();
      setQueryHistory(localHistory);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Open the delete confirmation dialog
  const openDeleteDialog = (event: React.MouseEvent, historyItem: QueryHistory) => {
    // Stop the event from propagating to the parent (which would trigger the history item click)
    event.stopPropagation();
    setItemToDelete(historyItem);
    setDeleteDialogOpen(true);
  };

  // Close the delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  // Start a new conversation
  const startNewConversation = () => {
    setQuery('');
    setResults(null);
    setConversationMessages([]);
    setError(null);
    setThreadId('');
    setSelectedHistoryId(null);
    // Don't reset filters - keep current filter state
  };

  // Handle deleting a history item after confirmation
  const handleDeleteHistoryItem = async () => {
    if (!itemToDelete || !itemToDelete.id) {
      console.error('Cannot delete history item without an ID');
      closeDeleteDialog();
      return;
    }

    try {
      // Delete from server first
      await deleteQueryHistory(itemToDelete.id);

      // Update the local state to remove the deleted item
      const updatedHistory = queryHistory.filter(item => item.id !== itemToDelete.id);
      setQueryHistory(updatedHistory);

      // If the current results are from this history item, clear them
      if (results && results.searchQuery === itemToDelete.query) {
        setResults(null);
        setConversationMessages([]);
        setQuery('');
        setIsReadOnly(false);
      }

      console.log(`Successfully deleted history item ${itemToDelete.id}`);
    } catch (error) {
      console.error(`Failed to delete history item ${itemToDelete.id}:`, error);
      // Even if server deletion fails, try to delete from localStorage
      deleteLocalQueryHistoryItem(itemToDelete.id);

      // Update the local state anyway (for better UX)
      const updatedHistory = queryHistory.filter(item => item.id !== itemToDelete.id);
      setQueryHistory(updatedHistory);
    } finally {
      closeDeleteDialog();
    }
  };

  // Handle clicking on a history item
  // Helper function to extract response text from a history item
  const extractResponseText = (result: any): string => {
    let responseText = '';

    if (typeof result === 'string') {
      try {
        const parsed = JSON.parse(result);
        responseText = parsed.response || '';
      } catch (e) {
        responseText = result;
      }
    } else if (typeof result === 'object') {
      if (result.response) {
        responseText = typeof result.response === 'string'
          ? result.response
          : JSON.stringify(result.response);
      }
    }

    return responseText;
  };

  const handleHistoryItemClick = async (historyItem: QueryHistory) => {
    console.log('Clicked history item:', historyItem);

    // Set the selected history item ID
    setSelectedHistoryId(historyItem.id);

    // Clear the query text when viewing history
    setQuery('');

    // Set the search box to read-only mode
    setIsReadOnly(true);

    // Clear any previous errors
    setError(null);
    setIsLoading(false);

    try {
      // Get the raw filters
      let rawFilters = historyItem.filters;

      // Parse filters if they're a string
      if (typeof rawFilters === 'string') {
        try {
          rawFilters = JSON.parse(rawFilters);
        } catch (e) {
          console.error('Error parsing filter string:', e);
        }
      }

      console.log('Parsed filters object:', rawFilters);
      console.log('Property names in raw filters:', Object.keys(rawFilters));

      // Create variables to hold our extracted values
      let extractedFacilityNames: string[] = [];
      let extractedClassificationNames: string[] = [];
      let extractedClassificationTypes: string[] = [];
      let extractedStartDate = '';
      let extractedEndDate = '';

      // Iterate through all properties in the raw filters object
      // This approach finds the data regardless of property naming
      for (const key in rawFilters) {
        const lowerKey = key.toLowerCase();

        // Log each key and its value for debugging
        console.log(`Checking key: ${key}, Value:`, rawFilters[key]);

        if (lowerKey.includes('facility')) {
          // Found facility names
          const value = rawFilters[key];
          if (Array.isArray(value)) {
            extractedFacilityNames = [...value];
          }
        } else if (lowerKey.includes('classification') && lowerKey.includes('name')) {
          // Found classification names
          const value = rawFilters[key];
          if (Array.isArray(value)) {
            extractedClassificationNames = [...value];
          }
        } else if (lowerKey.includes('classification') && lowerKey.includes('type')) {
          // Found classification types
          const value = rawFilters[key];
          if (Array.isArray(value)) {
            extractedClassificationTypes = [...value];
          }
        } else if (lowerKey.includes('date') || lowerKey.includes('range')) {
          // Found date range
          const value = rawFilters[key];
          if (typeof value === 'object' && value !== null) {
            // Check for various date properties
            for (const dateKey in value) {
              const lowerDateKey = dateKey.toLowerCase();
              if (lowerDateKey.includes('start')) {
                extractedStartDate = value[dateKey] || '';
              } else if (lowerDateKey.includes('end')) {
                extractedEndDate = value[dateKey] || '';
              }
            }
          }
        }
      }

      // Log what we extracted
      console.log('Extracted values:', {
        facilityNames: extractedFacilityNames,
        classificationNames: extractedClassificationNames,
        classificationTypes: extractedClassificationTypes,
        startDate: extractedStartDate,
        endDate: extractedEndDate
      });

      // Set the state variables with our extracted values
      setLocalFacilityNames(extractedFacilityNames);
      setLocalClassificationNames(extractedClassificationNames);
      setLocalClassificationTypes(extractedClassificationTypes);
      setLocalStartDate(extractedStartDate);
      setLocalEndDate(extractedEndDate);
    } catch (error) {
      console.error('Error processing history item filters:', error);
    }

    console.log('Loading history item:', historyItem.query);

    // Check if this history item has a threadId
    const clickedThreadId = historyItem.threadId || historyItem.result?.threadId;

    if (clickedThreadId) {
      console.log('Found threadId:', clickedThreadId, '- Loading full conversation thread');

      try {
        // Fetch all messages for this thread from the server
        const threadItems = await getQueryHistoryByThread(clickedThreadId);

        console.log(`Fetched ${threadItems.length} messages in thread ${clickedThreadId} from server`);

        // Sort by creation date (oldest first)
        threadItems.sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());

        // Create conversation messages for all items in the thread
        const messages: ConversationMessage[] = [];

        threadItems.forEach(item => {
          const responseText = extractResponseText(item.result);

          // Add user message
          messages.push({
            id: `user-${item.id}`,
            role: 'user',
            content: item.query,
            timestamp: item.createdDate
          });

          // Add assistant message
          messages.push({
            id: `assistant-${item.id}`,
            role: 'assistant',
            content: responseText,
            timestamp: item.createdDate
          });
        });

        setConversationMessages(messages);

        // Set the threadId so the conversation can continue
        setThreadId(clickedThreadId);

        // Set the results to the last item in the thread for backward compatibility
        if (threadItems.length > 0) {
          const lastItem = threadItems[threadItems.length - 1];
          const lastResponseText = extractResponseText(lastItem.result);

          const processedResult: ProcessedResult = {
            searchQuery: lastItem.query,
            response: lastResponseText,
            threadId: clickedThreadId
          };

          setResults(processedResult);
        }
      } catch (error) {
        console.error('Failed to load thread messages:', error);
        setError('Failed to load conversation thread. Please try again.');
      }
    } else {
      // No threadId - fall back to single item display
      console.log('No threadId found - displaying single query/response');

      if (historyItem.result) {
        const responseText = extractResponseText(historyItem.result);

        // Create a properly structured ProcessedResult
        const processedResult: ProcessedResult = {
          searchQuery: historyItem.query,
          response: responseText
        };

        // Set the results state
        setResults(processedResult);

        // Populate conversation messages for ChatGPT-style display
        const userMessage: ConversationMessage = {
          id: `user-${historyItem.id}`,
          role: 'user',
          content: historyItem.query,
          timestamp: historyItem.createdDate
        };

        const assistantMessage: ConversationMessage = {
          id: `assistant-${historyItem.id}`,
          role: 'assistant',
          content: responseText,
          timestamp: historyItem.createdDate
        };

        setConversationMessages([userMessage, assistantMessage]);
      }
    }
  };

  // Format date for display in UK format
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return dateString;
    }
  };

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Expose toggleSidebar to parent (modal header History button)
  useImperativeHandle(ref, () => ({
    toggleSidebar,
  }));

  // Copy message to clipboard
  const handleCopyMessage = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(messageId);
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  // Toggle summary expansion
  const toggleSummary = (messageId: string) => {
    setExpandedSummaries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  // Effect to update query when selectedQuestion changes - DISABLED to prevent auto-population
  // useEffect(() => {
  //   if (selectedQuestion) {
  //     setQuery(selectedQuestion);
  //   }
  // }, [selectedQuestion]);

  // Debug effect to log filter state changes
  useEffect(() => {
    console.log('Filter state updated:', {
      facilityNames: localFacilityNames,
      classificationNames: localClassificationNames,
      classificationTypes: localClassificationTypes,
      startDate: localStartDate,
      endDate: localEndDate
    });
  }, [localFacilityNames, localClassificationNames, localClassificationTypes, localStartDate, localEndDate]);

  if (!mounted) {
    return null;
  }



  const handleSearch = async () => {
    if (!query.trim()) return;

    console.group('Processing question');
    console.log('Query:', query);
    console.log('Filters:', {
      facilityNames: localFacilityNames,
      classificationNames: localClassificationNames,
      classificationTypes: localClassificationTypes,
      startDate: localStartDate,
      endDate: localEndDate
    });
    console.log('Current Thread ID:', threadId);

    const currentQuery = query.trim();
    setQuery('');

    const userMessage: ConversationMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: currentQuery,
      timestamp: new Date().toISOString()
    };
    setConversationMessages(prev => [...prev, userMessage]);

    const phrases = ['Analysing...', 'Thinking...', 'Reviewing...', 'Processing...'];
    setLoadingPhrase(phrases[Math.floor(Math.random() * phrases.length)]);
    setIsLoading(true);
    setError(null);

    try {
      const result = await processQuestion(
        currentQuery,
        threadId,
        localFacilityNames,
        localClassificationNames,
        localClassificationTypes,
        localStartDate,
        localEndDate
      );

      console.log('Received result:', result);
      setResults(result);

      // Add assistant message to conversation
      const assistantMessage: ConversationMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: result.response,
        timestamp: new Date().toISOString()
      };
      setConversationMessages(prev => [...prev, assistantMessage]);

      // Update thread ID if backend returned a new one
      if (result.threadId) {
        console.log('Backend returned new thread ID:', result.threadId);
        setThreadId(result.threadId);
      }

      // Refresh history after new query
      loadQueryHistory();
    } catch (err) {
      console.error('Error processing question:', err);

      // Handle specific error types
      if (err instanceof Error && err.message.includes('Failed to fetch')) {
        setError(
          'Network error: Failed to connect to the Azure OpenAI service. ' +
          'Please check your internet connection and ensure the Azure OpenAI endpoint is accessible. ' +
          'See browser console for detailed logs.'
        );
      } else if (err instanceof Error && err.message.includes('Azure OpenAI endpoint or API key is not configured')) {
        setError(
          'Configuration error: Azure OpenAI is not properly configured. ' +
          'Please check that REACT_APP_AZURE_ENDPOINT and REACT_APP_AZURE_API_KEY are set in your .env.local file.'
        );
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred while processing your question');
      }
    } finally {
      console.groupEnd();
      setIsLoading(false);
    }
  };

  return (
    <div ref={componentRef} className={classes.root} style={isPWA ? { padding: 0, margin: 0 } : undefined}>
      <Card className={isPWA ? classes.cardPWA : classes.card}>
        <CardContent>
          <div className={isPWA ? classes.drawerContainerPWA : classes.drawerContainer}>
            <Drawer
              classes={{ paper: classes.drawerPaper }}
              variant={isPWA ? 'temporary' : 'persistent'}
              anchor="left"
              open={sidebarOpen}
              onClose={isPWA ? () => setSidebarOpen(false) : undefined}
              ModalProps={isPWA ? { keepMounted: true } : undefined}
            >
              <div className={classes.sidebarHeader}>
                <Typography className={classes.sidebarTitle}>Conversations</Typography>
                <MuiButton
                  className={classes.newChatLink}
                  size="small"
                  onClick={() => {
                    if (isReadOnly) {
                      setIsReadOnly(false);
                      setQuery('');
                      setResults(null);
                      setConversationMessages([]);
                      setError(null);
                      setThreadId('');
                      setSelectedHistoryId(null);
                      setLocalFacilityNames(propFacilityNames);
                      setLocalClassificationNames(propClassificationNames);
                      setLocalClassificationTypes(propClassificationTypes);
                      setLocalStartDate(propStartDate);
                      setLocalEndDate(propEndDate);
                    } else {
                      startNewConversation();
                    }
                  }}
                  startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>}
                >
                  New
                </MuiButton>
              </div>

              <div className={classes.sidebarContent}>
                {loadingHistory ? (
                  <div style={{ padding: 16, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </div>
                ) : queryHistory.length === 0 ? (
                  <div className={classes.noHistory}>
                    <Typography variant="body2">No query history found</Typography>
                  </div>
                ) : (
                  <List style={{ padding: 0 }}>
                    {queryHistory.map((item) => {
                      // Extract preview text from response
                      let previewText = '';
                      try {
                        // Try multiple ways to extract the response text
                        let responseText = '';

                        if (item.result) {
                          if (typeof item.result === 'string') {
                            // If result is a string, try to parse it
                            try {
                              const parsed = JSON.parse(item.result);
                              responseText = parsed.response || item.result;
                            } catch {
                              responseText = item.result;
                            }
                          } else if (typeof item.result === 'object') {
                            // If result is an object, get the response property
                            responseText = item.result.response || JSON.stringify(item.result);
                          }
                        }

                        if (responseText && typeof responseText === 'string') {
                          // Clean up the preview: remove markdown, newlines, and extra spaces
                          const cleanedText = responseText
                            .replace(/\*\*/g, '') // Remove bold markdown
                            .replace(/\*/g, '')   // Remove italic markdown
                            .replace(/\n/g, ' ')  // Replace newlines with spaces
                            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                            .trim();
                          previewText = cleanedText.substring(0, 80) + (cleanedText.length > 80 ? '...' : '');
                        }
                      } catch (e) {
                        console.error('Error extracting preview:', e, item);
                      }

                      const isActive = selectedHistoryId === item.id;

                      return (
                        <ListItem
                          key={item.id}
                          className={`${classes.historyItem} ${isActive ? classes.historyItemActive : ''}`}
                          onClick={() => handleHistoryItemClick(item)}
                          style={{ cursor: 'pointer' }}
                        >
                          <ListItemText
                            primary={
                              <Typography className={classes.historyQuery}>
                                {item.query}
                              </Typography>
                            }
                            secondary={
                              <>
                                {previewText && (
                                  <div className={classes.historyPreview}>
                                    {previewText}
                                  </div>
                                )}
                                <div className={classes.historyDate}>
                                  {formatDate(item.createdDate)}
                                </div>
                              </>
                            }
                          />
                          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', alignSelf: 'stretch' }}>
                            <ChevronRightIcon style={{ fontSize: 18, color: 'rgba(255,255,255,0.3)' }} />
                            <IconButton
                              size="small"
                              onClick={(e) => openDeleteDialog(e, item)}
                              className={classes.historyItemDelete}
                              style={{ padding: 3, color: 'rgba(255,255,255,0.4)' }}
                              title="Delete chat"
                            >
                              <DeleteIcon style={{ fontSize: 14 }} />
                            </IconButton>
                          </div>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </div>


            </Drawer>
            {!isPWA && (
              <IconButton
                color="primary"
                size="small"
                className={sidebarOpen ? classes.toggleButton : classes.toggleButtonCollapsed}
                onClick={toggleSidebar}
              >
                {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </IconButton>
            )}
          </div>

            {/* Main Content */}
            <div className={classes.mainContent} style={{ marginLeft: isPWA ? 0 : (sidebarOpen ? `${SIDEBAR_WIDTH}px` : 0), height: isPWA ? '100vh' : undefined }}>
              {isPWA && (
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-card-bg)' }}>
                  <IconButton size="small" onClick={() => setSidebarOpen(true)} style={{ marginRight: 8 }}>
                    <MenuIcon />
                  </IconButton>
                  <img src="images/logos/logomark.png" alt="Signals" style={{ width: 28, height: 28, marginRight: 8 }} />
                  <Typography variant="subtitle1" style={{ flex: 1, fontWeight: 600 }}>Signals</Typography>
                  <IconButton size="small" onClick={() => { localStorage.removeItem('accessToken'); localStorage.removeItem('currentUserEmail'); window.location.href = '/'; }} title="Sign out">
                    <ExitToAppIcon style={{ fontSize: 20 }} />
                  </IconButton>
                </div>
              )}
              {error && (
                <div className={classes.errorContainer}>
                  <ErrorIcon className={classes.errorIcon} />
                  <div className={classes.errorContent}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      Error: {error.includes(':') ? error.split(':')[0] + ':' : 'Request Failed'}
                    </Typography>
                    <Typography variant="body2">
                      {error.includes(':') ? error.split(':').slice(1).join(':').trim() : error}
                    </Typography>
                    {error.includes('Failed to fetch') && (
                      <Typography variant="body2" style={{ marginTop: '8px' }}>
                        Tip: Open the browser console (F12) for detailed error information.
                      </Typography>
                    )}
                  </div>
                </div>
              )}

              {/* Welcome screen when no conversation */}
              {!results && conversationMessages.length === 0 && !isLoading && (
                <div className={classes.welcomeContainer}>
                  <img src="images/logos/logomark.png" alt="Signals" style={{ width: 72, height: 72, marginBottom: 24 }} />
                  <Typography className={classes.welcomeTitle}>
                    Welcome to COMET Signals
                  </Typography>
                  <Typography className={classes.welcomeSubtitle}>
                    Your AI-powered assistant for analysing EHS incident data, uncovering trends, and generating actionable safety insights.
                  </Typography>
                  <div className={classes.suggestionGrid}>
                    <div className={classes.suggestionCard} onClick={() => { setQuery('What are the top safety incidents this quarter?'); }}>
                      <StarsIcon className={classes.suggestionIcon} />
                      <span className={classes.suggestionText}>What are the top safety incidents this year?</span>
                    </div>
                    <div className={classes.suggestionCard} onClick={() => { setQuery('Show me trends in near-miss reports'); }}>
                      <StarsIcon className={classes.suggestionIcon} />
                      <span className={classes.suggestionText}>Show me trends in near-miss reports</span>
                    </div>
                    <div className={classes.suggestionCard} onClick={() => { setQuery('Which facilities have the highest incident rates?'); }}>
                      <StarsIcon className={classes.suggestionIcon} />
                      <span className={classes.suggestionText}>Which facilities have the highest incident rates?</span>
                    </div>
                    <div className={classes.suggestionCard} onClick={() => { setQuery('Summarize root causes for slip and fall incidents'); }}>
                      <StarsIcon className={classes.suggestionIcon} />
                      <span className={classes.suggestionText}>Summarise root causes for slip and fall incidents</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Conversation area */}
              {(results || conversationMessages.length > 0) && (
                <div ref={chatScrollRef} className={classes.resultsContainer}>
                  {/* Response Section */}
                  <div style={{ display: 'contents' }}>
                    {/* Filter section - hidden for now */}
                    <div style={{ display: 'none' }} className={classes.filterChips}>
                      {/* Facility Names Filter */}
                      <div className={classes.filterChip} style={{
                        opacity: localFacilityNames.length > 0 ? 1 : 0.7,
                        backgroundColor: localFacilityNames.length > 0 ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                        borderColor: localFacilityNames.length > 0 ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                      }}>
                        <FilterIcon fontSize="small" style={{
                          fontSize: '16px',
                          marginRight: '8px',
                          color: localFacilityNames.length > 0 ? 'var(--color-info)' : 'var(--color-icon-default)'
                        }} />
                        <strong>Facilities:</strong>&nbsp;
                        {localFacilityNames.length > 0 ? localFacilityNames.join(', ') : 'All Facilities'}
                      </div>

                      {/* Classification Names Filter */}
                      <div className={classes.filterChip} style={{
                        opacity: localClassificationNames.length > 0 ? 1 : 0.7,
                        backgroundColor: localClassificationNames.length > 0 ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                        borderColor: localClassificationNames.length > 0 ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                      }}>
                        <FilterIcon fontSize="small" style={{
                          fontSize: '16px',
                          marginRight: '8px',
                          color: localClassificationNames.length > 0 ? 'var(--color-info)' : 'var(--color-icon-default)'
                        }} />
                        <strong>Classifications:</strong>&nbsp;
                        {localClassificationNames.length > 0 ? localClassificationNames.join(', ') : 'All Classifications'}
                      </div>

                      {/* Classification Types Filter */}
                      <div className={classes.filterChip} style={{
                        opacity: localClassificationTypes.length > 0 ? 1 : 0.7,
                        backgroundColor: localClassificationTypes.length > 0 ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                        borderColor: localClassificationTypes.length > 0 ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                      }}>
                        <FilterIcon fontSize="small" style={{
                          fontSize: '16px',
                          marginRight: '8px',
                          color: localClassificationTypes.length > 0 ? 'var(--color-info)' : 'var(--color-icon-default)'
                        }} />
                        <strong>Types:</strong>&nbsp;
                        {localClassificationTypes.length > 0 ? localClassificationTypes.join(', ') : 'All Types'}
                      </div>

                      {/* Date Range Filter */}
                      <div className={classes.filterChip} style={{
                        opacity: (localStartDate && localEndDate) ? 1 : 0.7,
                        backgroundColor: (localStartDate && localEndDate) ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                        borderColor: (localStartDate && localEndDate) ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                      }}>
                        <FilterIcon fontSize="small" style={{
                          fontSize: '16px',
                          marginRight: '8px',
                          color: (localStartDate && localEndDate) ? 'var(--color-info)' : 'var(--color-icon-default)'
                        }} />
                        <strong>Date Range:</strong>&nbsp;
                        {(localStartDate && localEndDate) ?
                          `${new Date(localStartDate).toLocaleDateString()} - ${new Date(localEndDate).toLocaleDateString()}` :
                          'All Dates'}
                      </div>
                    </div>

                    <Paper className={classes.responseContainer} elevation={0}>
                    {/* ChatGPT-style conversation display */}
                    {conversationMessages.length > 0 && (
                      <div className={classes.conversationContainer}>
                        {conversationMessages.map((message) => (
                          <div key={message.id} className={classes.messageWrapper}>
                            {message.role === 'user' ? (
                              <>
                                <div className={classes.messageLabelRow} style={{ justifyContent: 'flex-end' }}>
                                  <span className={classes.messageLabel}>You</span>
                                  <span className={classes.messageTimestamp}>· {message.timestamp ? formatDate(message.timestamp) : ''}</span>
                                </div>
                                <div className={`${classes.messageRow} ${classes.messageRowUser}`}>
                                  <div className={classes.userMessage}>
                                    <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                                      {message.content}
                                    </Typography>
                                  </div>
                                  <div className={classes.userAvatarIcon}>
                                    <span style={{ fontSize: '0.7rem', fontWeight: 700 }}>👤</span>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                              <div className={classes.messageLabelRow}>
                                <div className={classes.messageAvatar}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#df6d2e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
                                    <path d="M20 3v4"/>
                                    <path d="M22 5h-4"/>
                                  </svg>
                                </div>
                                <span className={classes.messageLabel}>COMET</span>
                                <span className={classes.messageTimestamp}>· {message.timestamp ? formatDate(message.timestamp) : ''}</span>
                              </div>
                              <div className={`${classes.messageRow} ${classes.messageRowAssistant}`}>
                                <div style={{ width: 40, flexShrink: 0 }} />
                                <div className={classes.assistantMessage}>
                                {/* Render assistant response with simplified markdown */}
                                {(() => {
                                  const responseText = message.content;
                                  const isExpanded = expandedSummaries.has(message.id);

                                  let processedContent = responseText.replace(/CO2/g, 'CO₂');
                                  processedContent = processedContent
                                    .replace(/^\s*\*{2,}\s*$/gm, '')
                                    .replace(/\*\*\s*\*\*/g, '')
                                    .replace(/\n{3,}/g, '\n\n');

                                  // Split into main content and recommendation if present
                                  const recommendationMatch = processedContent.match(/\*\*Recommendation\*\*:|Recommendation:|\*\*Recommendations\*\*:|Recommendations:/i);

                                  let mainContent = processedContent;
                                  let recommendationContent = '';

                                  if (recommendationMatch) {
                                    const splitIndex = recommendationMatch.index;
                                    mainContent = processedContent.substring(0, splitIndex).trim();
                                    recommendationContent = processedContent.substring(splitIndex)
                                      .replace(/\*\*Recommendation\*\*:|Recommendation:|\*\*Recommendations\*\*:|Recommendations:/i, '')
                                      .replace(/^\s*\*{2,}\s*$/gm, '')
                                      .replace(/\n{3,}/g, '\n\n')
                                      .trim();
                                  }

                                  mainContent = mainContent
                                    .replace(/^\s*\*{2,}\s*$/gm, '')
                                    .replace(/\n{3,}/g, '\n\n')
                                    .trim();

                                  // Show full content by default; only allow collapsing very long responses
                                  const lineCount = mainContent.split('\n').filter(line => line.trim()).length;
                                  const isLongResponse = lineCount > 30;

                                  // Default to expanded (show everything); collapsed only after user clicks "Show less"
                                  const contentToDisplay = (!isLongResponse || isExpanded) ? mainContent : mainContent.split('\n').slice(0, 15).join('\n');

                                  return (
                                    <>
                                      {/* Main content */}
                                      <div style={{ marginBottom: '8px' }}>
                                        <div className={classes.markdownBody}>
                                          <ReactMarkdown>
                                            {contentToDisplay}
                                          </ReactMarkdown>
                                        </div>
                                      </div>

                                      {/* Collapse button - only for very long responses */}
                                      {isLongResponse && (
                                        <button
                                          className={classes.expandButton}
                                          onClick={() => toggleSummary(message.id)}
                                        >
                                          {isExpanded ? (
                                            <>
                                              <span>Show less</span>
                                              <ExpandLessIcon style={{ fontSize: '16px' }} />
                                            </>
                                          ) : (
                                            <>
                                              <span>Show full response</span>
                                              <ExpandMoreIcon style={{ fontSize: '16px' }} />
                                            </>
                                          )}
                                        </button>
                                      )}

                                      {/* Recommendation section - always visible */}
                                      {recommendationContent && (
                                        <div className={classes.recommendationSection}>
                                          <Typography variant="subtitle1" className={classes.recommendationTitle}>
                                            Recommendation
                                          </Typography>
                                          <div className={`${classes.recommendationContent} ${classes.markdownBody}`}>
                                            <ReactMarkdown>
                                              {recommendationContent}
                                            </ReactMarkdown>
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                                </div>
                              </div>
                              <div className={classes.messageActions} style={{ paddingLeft: 52 }}>
                                <IconButton
                                  className={classes.copyButton}
                                  onClick={() => handleCopyMessage(message.id, message.content)}
                                  size="small"
                                  title={copiedId === message.id ? "Copied!" : "Copy"}
                                >
                                  {copiedId === message.id ? (
                                    <CheckIcon className={classes.copyButtonIcon} />
                                  ) : (
                                    <CopyIcon className={classes.copyButtonIcon} />
                                  )}
                                </IconButton>
                                <IconButton className={classes.copyButton} size="small" title="Helpful">
                                  <ThumbUpIcon className={classes.copyButtonIcon} />
                                </IconButton>
                                <IconButton className={classes.copyButton} size="small" title="Not helpful">
                                  <ThumbDownIcon className={classes.copyButtonIcon} />
                                </IconButton>
                              </div>
                              </>
                            )}
                          </div>
                        ))}
                        {isLoading && (
                          <div className={classes.messageWrapper}>
                            <div className={classes.messageLabelRow}>
                              <div className={classes.messageAvatar}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#df6d2e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
                                  <path d="M20 3v4"/>
                                  <path d="M22 5h-4"/>
                                </svg>
                              </div>
                              <span className={classes.messageLabel}>COMET</span>
                            </div>
                            <div className={`${classes.messageRow} ${classes.messageRowAssistant}`}>
                              <div style={{ width: 40, flexShrink: 0 }} />
                              <div className={classes.assistantMessage} style={{ padding: '16px 20px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-secondary)' }}>
                                  <CircularProgress size={14} style={{ color: '#df6d2e' }} />
                                  <span style={{ fontSize: '0.9rem' }}>{loadingPhrase}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Paper>
                </div>
              </div>
            )}

            {/* Chat Input */}
            <div className={classes.searchContainer}>
              <IconButton
                className={`${classes.inputIconButton} ${isRecording ? classes.micRecording : ''}`}
                title={isRecording ? 'Stop recording' : 'Use voice input'}
                onClick={toggleVoiceRecording}
              >
                {isRecording ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
                ) : (
                  <MicIcon style={{ fontSize: 18 }} />
                )}
              </IconButton>
              <div className={classes.textFieldContainer}>
                <TextField
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={isRecording ? 'Speak now...' : 'Ask anything...'}
                  variant="outlined"
                  multiline
                  minRows={1}
                  maxRows={6}
                  className={classes.textField}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
              </div>
              <MuiButton
                className={classes.sendButton}
                onClick={handleSearch}
                disabled={!query.trim() || isLoading}
                startIcon={isPWA ? undefined : (isLoading ? <CircularProgress size={14} style={{ color: 'white' }} /> : <SendIcon style={{ fontSize: 16 }} />)}
                style={isPWA ? { minWidth: 44, padding: 0, width: 44 } : undefined}
              >
                {isPWA
                  ? (isLoading ? <CircularProgress size={14} style={{ color: 'white' }} /> : <SendIcon style={{ fontSize: 16 }} />)
                  : (isLoading ? '...' : 'Send')}
              </MuiButton>
            </div>
            </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this query from your history?
            {itemToDelete && (
              <Typography
                variant="body2"
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: 'var(--color-page-bg)',
                  borderRadius: '4px',
                  fontStyle: 'italic'
                }}
              >
                "{itemToDelete.query}"
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={closeDeleteDialog} color="primary">
            Cancel
          </MuiButton>
          <MuiButton onClick={handleDeleteHistoryItem} color="secondary" autoFocus>
            Delete
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
});

export default VectorSearch;
